import React, { useMemo, useCallback } from 'react'
import type { FieldError as HookFieldError } from 'react-hook-form'

import { cn } from '@/lib/utils'
import FieldError from '@/components/livecontact/FieldError'
import LcIcon from '@/components/livecontact/icons'
import {
  INPUT_CLASS,
  INPUT_ERROR_CLASS,
  LABEL_CLASS,
  NOTE_CLASS,
} from '@/components/livecontact/fieldStyles'

export type StepperFieldProps = {
  className?: string
  classInput?: string
  classLabel?: string
  classInnerInput?: string
  classLabelInner?: string
  label?: React.ReactNode
  note?: string
  value?: number | string
  defaultValue?: number | string
  onChange?: (value: number | string) => void
  onBlur?: () => void
  placeholder?: string
  required?: boolean
  disabled?: boolean
  readOnly?: boolean
  error?: HookFieldError
  autoFocus?: boolean
  step?: number
  min?: number
  max?: number
}

const STEP_BUTTON =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-[8px] bg-surface text-ink-2 shadow-input outline-none ' +
  'transition-[transform,color,background-color] duration-[140ms] ease-out-strong hover:text-ink active:scale-[0.94] focus-visible:ring-2 focus-visible:ring-brand/40 ' +
  'disabled:cursor-not-allowed disabled:opacity-40 disabled:active:scale-100'

/** Number input flanked by −/+ 32px buttons. */
const StepperField: React.FC<StepperFieldProps> = ({
  className,
  classInput,
  classLabel,
  classInnerInput,
  classLabelInner,
  label,
  note,
  value,
  defaultValue,
  onChange,
  onBlur,
  placeholder,
  required,
  disabled,
  readOnly,
  error,
  autoFocus,
  step = 2,
  min = 0,
  max,
}) => {
  const numericValue = useMemo(() => {
    if (value === '' || value === undefined || value === null) return 0
    const n = typeof value === 'number' ? value : Number(value)
    return Number.isNaN(n) ? 0 : n
  }, [value])

  const canDecrement = useMemo(
    () => !disabled && !readOnly && numericValue > (min ?? -Infinity),
    [disabled, min, numericValue, readOnly]
  )
  const canIncrement = useMemo(
    () => !disabled && !readOnly && (max == null || numericValue < max),
    [disabled, max, numericValue, readOnly]
  )

  const handleDec = useCallback(() => {
    if (!onChange) return
    onChange(Math.max(min ?? -Infinity, numericValue - step))
  }, [min, numericValue, onChange, step])

  const handleInc = useCallback(() => {
    if (!onChange) return
    onChange(Math.min(max ?? Infinity, numericValue + step))
  }, [max, numericValue, onChange, step])

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value
      if (!onChange) return
      if (val === '') {
        onChange('')
        return
      }
      const n = Number(val)
      if (Number.isNaN(n)) return
      let next = n
      if (min != null) next = Math.max(min, next)
      if (max != null) next = Math.min(max, next)
      onChange(next)
    },
    [onChange, min, max]
  )

  return (
    <div className={className}>
      {label && (
        <div className={cn(LABEL_CLASS, classLabelInner)}>
          <span className={classLabel}>{label}</span>
        </div>
      )}
      <div className={cn('flex items-center gap-2', classInnerInput)}>
        <button
          type="button"
          aria-label={`Decrease by ${step}`}
          onClick={handleDec}
          disabled={!canDecrement}
          className={STEP_BUTTON}
        >
          <LcIcon name="minus" />
        </button>
        <input
          className={cn(INPUT_CLASS, 'min-w-0 flex-1 text-center tabular-nums', error && INPUT_ERROR_CLASS, classInput)}
          type="number"
          value={value}
          defaultValue={defaultValue}
          onChange={handleInputChange}
          onBlur={onBlur}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          step={step}
          autoFocus={autoFocus}
          aria-invalid={!!error}
        />
        <button
          type="button"
          aria-label={`Increase by ${step}`}
          onClick={handleInc}
          disabled={!canIncrement}
          className={STEP_BUTTON}
        >
          <LcIcon name="plus" />
        </button>
      </div>
      {note && <p className={NOTE_CLASS}>{note}</p>}
      <FieldError message={error?.message} />
    </div>
  )
}

export default StepperField
